import { useState, useEffect } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { SettingsNavigation } from './SettingsNavigation'
import { TopNavigation } from '../TopNavigation'
import { SettingsCompany } from './SettingsCompany'
import { SettingsUsers } from './SettingsUsers'
import { SettingsCustomers } from '@/components/settings/customers/SettingsCustomers'
import { useAuth } from '../../auth/useAuth'
import { SettingsAuthentication } from './SettingsAuthentication'
import { SettingsIntegrations } from './SettingsIntegrations'
import { SettingsSendingDomains } from './SettingsSendingDomains'
import { SettingsReplyToAddresses } from './SettingsReplyToAddresses'
import { SettingsSuppressions } from './SettingsSuppressions'
import { KnowledgeBasePanel } from '@/components/knowledgeBase/KnowledgeBasePanel'
import { KnowledgeBaseLevel } from '@/types/knowledgeBase'

interface SettingsProps {
  activeSection?: string
  clientCompanyId?: string
  onBackToApp?: () => void
}

export function Settings({ activeSection: initialSection, clientCompanyId, onBackToApp }: SettingsProps) {
  const [activeSection, setActiveSection] = useState(initialSection || 'settings/company')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const auth = useAuth()
  const location = useLocation()

  // Update activeSection when route changes
  useEffect(() => {
    if (initialSection) {
      setActiveSection(initialSection)
    }
  }, [initialSection])

  // Workspace selection is managed by Home/bootstrap; settings URLs are company-level

  const getNavigationUrl = (section: string) => {
    const effectiveClientCompanyId = clientCompanyId || ''

    if (effectiveClientCompanyId) {
      return `/company/${effectiveClientCompanyId}/${section}`
    }
    // If we don't have the required data, we can't navigate - this shouldn't happen in normal flow
    console.error('Missing clientCompanyId for navigation')
    return `/company/${effectiveClientCompanyId}/${section}`
  }

  const handleSectionChange = (section: string) => {
    setActiveSection(section)
    const url = getNavigationUrl(section)
    location.route(url)
  }

  const handleBackToApp = () => {
    if (onBackToApp) {
      onBackToApp()
      return
    }

    const effectiveClientCompanyId = clientCompanyId || ''
    if (!effectiveClientCompanyId) {
      location.route('/')
      return
    }

  // Let the company bootstrap route handle workspace selection
    location.route(`/company/${effectiveClientCompanyId}`)
  }

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const renderMainContent = () => {
    switch (activeSection) {
      case 'settings/company':
        return (
          <SettingsCompany
            onNavigateHome={onBackToApp}
            companyId={clientCompanyId ?? auth.userProfile?.ClientCompanyId}
          />
        )
      case 'settings/customers':
        return <SettingsCustomers clientCompanyId={clientCompanyId} />
      case 'settings/authentication':
        return <SettingsAuthentication clientCompanyId={clientCompanyId} />
      case 'settings/integrations':
        return <SettingsIntegrations />
      case 'settings/sending-domains':
        return <SettingsSendingDomains />
      case 'settings/reply-to-addresses':
        return <SettingsReplyToAddresses />
      case 'settings/suppression-list':
        return <SettingsSuppressions />
      case 'settings/users':
        return <SettingsUsers clientCompanyId={clientCompanyId} />
      case 'settings/knowledge-base':
        return (
          <div className="p-6">
            <KnowledgeBasePanel
              clientCompanyId={clientCompanyId ?? auth.userProfile?.ClientCompanyId ?? ''}
              level={KnowledgeBaseLevel.Company}
              isAdmin={true}
            />
          </div>
        )
      default:
        return (
          <SettingsCompany
            onNavigateHome={onBackToApp}
            companyId={clientCompanyId ?? auth.userProfile?.ClientCompanyId}
          />
        )
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex">
      {/* Settings Navigation */}
      <SettingsNavigation
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onBackToApp={handleBackToApp}
        isOpen={isMobileMenuOpen}
        onToggle={handleMobileMenuToggle}
      />

      {/* Main content area */}
      <div className="flex-1 lg:ml-64 flex flex-col">
        {/* Top Navigation */}
        <TopNavigation
          onMenuToggle={handleMobileMenuToggle}
          hideCompanyWorkspaceSelector={true}
        />

        {/* Main content */}
        <main className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-800">
          <div>
            {renderMainContent()}
          </div>
        </main>
      </div>
    </div>
  )
}
